import React, { useState } from "react";
import "./CostSupportInfo.css";

type TabKey = "kotsu" | "faq" | "shikumi";

const ChevronDown = () => (
  <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M4 6l4 4 4-4" />
  </svg>
);

type AccordionItemProps = {
  id: string;
  badge: React.ReactNode;
  question: string;
  openIds: Set<string>;
  onToggle: (id: string) => void;
  children: React.ReactNode;
};

function AccordionItem({ id, badge, question, openIds, onToggle, children }: AccordionItemProps) {
  const isOpen = openIds.has(id);
  return (
    <div className={`csup-item${isOpen ? " csup-open" : ""}`}>
      <div className="csup-iq" onClick={() => onToggle(id)}>
        {badge}
        <span className="csup-iq-text">{question}</span>
        <span className="csup-chev">
          <ChevronDown />
        </span>
      </div>
      <div className="csup-ia">
        <div className="csup-ia-inner">{children}</div>
      </div>
    </div>
  );
}

type Props = {
  sectionId?: string;
};

export function FixedCostSupportInfo({ sectionId = "support-section" }: Props) {
  const [activeTab, setActiveTab] = useState<TabKey>("kotsu");
  const [openIds, setOpenIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <div className="csup-root" id={sectionId}>
      {/* セクションヘッダー */}
      <div className="csup-sec-header">
        <div className="csup-sec-icon">
          <svg viewBox="0 0 20 20">
            <path d="M10 2C5.6 2 2 5.6 2 10s3.6 8 8 8 8-3.6 8-8-3.6-8-8-8zm1 13H9v-2h2v2zm0-4H9c0-3.25 3-3 3-5 0-1.1-.9-2-2-2s-2 .9-2 2H7c0-1.65 1.35-3 3-3s3 1.35 3 3c0 2-3 2.25-3 5z" />
          </svg>
        </div>
        <span className="csup-sec-title">固定費入力 サポート情報</span>
        <span className="csup-sec-date">最終更新：2026年4月</span>
      </div>

      {/* タブ */}
      <div className="csup-tabs">
        <button
          className={`csup-tab csup-tab-kotsu${activeTab === "kotsu" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("kotsu")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M2 11h12v1.5a1 1 0 01-1 1H3a1 1 0 01-1-1V11zm6-9.5a5 5 0 015 5c0 1.8-1 3.4-2.4 4.3L10 12H6l-.6-1.2A5 5 0 018 1.5zm1 2.3V5h1.5v1.5H9V8H7.5V6.5H6V5h1.5V3.8h1.5z" />
          </svg>
          運用のコツ
        </button>
        <button
          className={`csup-tab csup-tab-faq${activeTab === "faq" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("faq")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M14 2H2a1 1 0 00-1 1v8a1 1 0 001 1h2v2.5l3-2.5h7a1 1 0 001-1V3a1 1 0 00-1-1zM8 9.5a1 1 0 110-2 1 1 0 010 2zm1-3.3c0 .4-.2.7-.5.9L8 7.4V7h-.5V5.8l.8-.5c.2-.1.2-.2.2-.3 0-.3-.2-.5-.5-.5s-.5.2-.5.5H6c0-.9.7-1.5 1.5-1.5S9 5.3 9 6.2z" />
          </svg>
          よくある質問
        </button>
        <button
          className={`csup-tab csup-tab-shikumi${activeTab === "shikumi" ? " csup-active" : ""}`}
          onClick={() => setActiveTab("shikumi")}
        >
          <svg viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 11.5a1 1 0 110-2 1 1 0 010 2zm.7-4.2c-.4.3-.7.6-.7 1v.2H6.5v-.2c0-.9.5-1.6 1.1-2 .4-.3.6-.6.6-1 0-.6-.5-1-1.2-1-.6 0-1.1.4-1.2.9H4.3C4.5 4.8 5.6 4 7 4c1.6 0 2.7 1 2.7 2.3 0 .9-.5 1.6-1 2z" />
          </svg>
          仕組みを知る
        </button>
      </div>

      {/* 運用のコツ */}
      <div className={`csup-panel${activeTab === "kotsu" ? " csup-active" : ""}`}>
        <AccordionItem
          id="kotsu-1"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="楽天出店プランはRMS右上の「ショップ名」をクリックいただき、「契約プラン」から確認が可能です"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>出店プランによって月額費用が異なりますので、プランが変わった月は更新してください。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-2"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="「複数月一括適用」機能で毎月の入力を効率化しましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>年間を通じて金額が変わらない科目は「複数月一括適用」機能を使うと、複数月に同じ金額を一度に登録できます。毎月手動で入力する必要がなくなり、入力ミスも防げます。</p>
          </div>
          <div className="csup-note csup-note-blue">
            年度切り替え時は「複数月一括適用」または「前年度からのコピー」を活用してください。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-3"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="自由入力欄に社内の有料システム費用を登録しておきましょう"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>自由入力には、在庫連携・一元管理ソフト・外注などの費用を登録しておくと、実態に近い利益が把握できます。</p>
            <p>科目名も自由に設定できるので、社内でわかりやすい名称をつけておきましょう。人件費・家賃・倉庫代などを入れていただくこともオススメです。</p>
          </div>
          <div className="csup-note csup-note-blue">
            科目名は「有料システム①（名称：◯◯）」のように編集できます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="kotsu-4"
          badge={<span className="csup-badge csup-badge-kotsu">コツ</span>}
          question="利用していないオプションサービスは必ずOFFにしてください"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>実際に契約・利用中の場合のみONにしてください。利用していないのにONのままにすると、固定費が実態より高く計算され、利益が低く表示されてしまいます。</p>
          </div>
          <div className="csup-note csup-note-amber">
            毎月の楽天請求書と照合して、契約状況に変化がないか確認しましょう。
          </div>
        </AccordionItem>
      </div>

      {/* よくある質問 */}
      <div className={`csup-panel${activeTab === "faq" ? " csup-active" : ""}`}>
        <AccordionItem
          id="faq-1"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="固定費を登録・変更すると、いつから利益に反映されますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>登録・保存した時点で、設定した月の利益計算に即時反映されます。過去月を遡って修正した場合も、該当月の利益データが更新されます。ただし当日分のデータは翌日以降に反映される場合があります。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-2"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="宅急便とメール便の送料はどちらも登録が必要ですか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>はい、なるべく正確に1配送あたりの平均送料のご入力をお願いします。</p>
          </div>
          <div className="csup-note csup-note-blue">
            一部のプランでは、2つ全ての配送方法を統合して、1配送あたりの平均値をご入力いただく場合もございます。
          </div>
        </AccordionItem>

        <AccordionItem
          id="faq-3"
          badge={<span className="csup-badge csup-badge-faq">Q</span>}
          question="「開始月（期首）」はどこで変更できますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>開始月は初期設定ページから変更できます。変更すると固定費入力の年度表示（例：2025/06〜2026/05）も連動して切り替わります。決算期や運用開始月に合わせて最初に設定しておくことをおすすめします。</p>
          </div>
          <div className="csup-note csup-note-blue">
            起算月の変更は固定費入力ではなく「初期設定」ページで行います。
          </div>
        </AccordionItem>
      </div>

      {/* 仕組みを知る */}
      <div className={`csup-panel${activeTab === "shikumi" ? " csup-active" : ""}`}>
        <AccordionItem
          id="shikumi-1"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="固定費はどのように利益計算に使われていますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>月次利益の計算では、売上から変動費（原価・物流費・楽天手数料・販促費）を差し引いた「限界利益」から、さらに固定費を差し引いた金額が「営業利益」となります。</p>
            <p>固定費の登録が漏れていると、営業利益が実態より大きく（良く）表示されるためご注意ください。</p>
          </div>
          <div className="csup-note csup-note-amber">
            固定費の登録は「営業利益」の精度に直結します。
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-2"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="自動取得されない経費や費用はありますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>現時点では、クーポン50枚発行以上分の費用、LINEの従量課金費用は、自動計算できておらず、手動での固定費自由入力欄への入力が必要です。</p>
            <p>楽天からの月次請求書を参照して、毎月概算値をご入力ください。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-3"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="変動費と固定費の違いは何ですか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>変動費は売上に連動して変わるコスト（商品原価・物流費・楽天手数料・販促費・広告費など）です。固定費は売上に関わらず毎月一定額かかるコスト（出店プラン料・有料システム費など）です。</p>
            <p>この２つを分けて管理することで、より正確な利益把握ができます。</p>
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-4"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="固定費を登録しないとどのような影響がありますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>固定費が未登録の場合、利益Naviでは「限界利益」しか正しく計算されず、営業利益算出の精度が下がります。</p>
            <p>正確な経営判断のために、固定費の登録をオススメしますが、一方で、大手企業のご担当者様などは、固定費を扱える権限も有していないため、限界利益額や限界利益率だけをKPIとして追っているケースも多くございます。</p>
            <p>御社の事情に合わせて登録いただけますと幸いです。</p>
          </div>
          <div className="csup-note csup-note-amber">
            固定費未登録の場合、営業利益の算出精度が下がります。
          </div>
        </AccordionItem>

        <AccordionItem
          id="shikumi-5"
          badge={<span className="csup-badge csup-badge-shikumi">仕組み</span>}
          question="過去月の固定費を修正するとデータはどうなりますか？"
          openIds={openIds}
          onToggle={toggle}
        >
          <div className="csup-ia-text">
            <p>過去月の固定費を修正・保存すると、その月の利益データが再計算されます。修正した内容は即時反映されるため、過去の利益推移が変わる場合があります。</p>
            <p>月次報告や経営会議の資料作成前に固定費の登録内容を確認しておくことをおすすめします。</p>
          </div>
        </AccordionItem>
      </div>
    </div>
  );
}
